import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCustomerType } from '../context/CustomerTypeContext';
import Logo from './Logo';
import './Navbar.css';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCustomerTypeDropdownOpen, setIsCustomerTypeDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { getTotalItems, clearCart } = useCart();
  const { customerType, selectCustomerType, isPrivate, isCorporate } = useCustomerType();
  const cartCount = getTotalItems();
  const isAdmin = location.pathname.startsWith('/admin');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCustomerTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  const customerTypeLabel = customerType
    ? isPrivate
      ? '👤 Private'
      : '🏢 Corporate'
    : 'Select customer type';

  const handleCustomerTypeChange = (newType) => {
    if (newType === 'corporate' && isPrivate) {
      // Clear cart when switching from private to corporate
      clearCart();
    }
    selectCustomerType(newType);
    setIsCustomerTypeDropdownOpen(false);
    setIsMenuOpen(false);
    // Redirect to home page
    navigate('/');
  };

  if (isAdmin) return null;

  return (
    <nav className="navbar">
      <div className="container">
        <div className="navbar-content">
          <Link to="/" className="navbar-brand">
            <Logo size="medium" useImage={true} imageSrc="/logo.png" />
            <span className="navbar-brand-text">MunchUps</span>
          </Link>

          <div className="navbar-mobile-actions">
            {isPrivate && (
              <Link
                to="/cart"
                className={`mobile-cart-link ${location.pathname === '/cart' ? 'active' : ''}`}
                aria-label="View cart"
              >
                <span className="mobile-cart-icon">🛒</span>
                {cartCount > 0 && <span className="cart-badge mobile-cart-badge">{cartCount}</span>}
              </Link>
            )}

            <button
              className="menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

          <ul className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
            <li>
              <Link 
                to="/" 
                className={location.pathname === '/' ? 'active' : ''}
                onClick={handleLinkClick}
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/products"
                className={location.pathname === '/products' ? 'active' : ''}
                onClick={handleLinkClick}
              >
                Products
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                className={location.pathname === '/about' ? 'active' : ''}
                onClick={handleLinkClick}
              >
                About Us
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className={location.pathname === '/contact' ? 'active' : ''}
                onClick={handleLinkClick}
              >
                Contact
              </Link>
            </li>
            <li>
              <Link
                to="/leave-review"
                className={location.pathname === '/leave-review' ? 'active' : ''}
                onClick={handleLinkClick}
              >
                Leave a Review
              </Link>
            </li>
            <li className="customer-type-toggle">
              <div className="customer-type-dropdown" ref={dropdownRef}>
                <button
                  className="customer-type-btn"
                  onClick={() => setIsCustomerTypeDropdownOpen(!isCustomerTypeDropdownOpen)}
                  aria-label="Change customer type"
                >
                  {customerTypeLabel}
                  <span className="dropdown-arrow">▼</span>
                </button>
                {isCustomerTypeDropdownOpen && (
                  <div className="customer-type-menu">
                    <button
                      className={`customer-type-option ${isPrivate ? 'active' : ''}`}
                      onClick={() => handleCustomerTypeChange('private')}
                    >
                      👤 Private Customer
                    </button>
                    <button
                      className={`customer-type-option ${isCorporate ? 'active' : ''}`}
                      onClick={() => handleCustomerTypeChange('corporate')}
                    >
                      🏢 Corporate Customer
                    </button>
                  </div>
                )}
              </div>
            </li>
            {isPrivate && (
              <li className="navbar-menu-cart-item">
                <Link
                  to="/cart"
                  className={`cart-link ${location.pathname === '/cart' ? 'active' : ''}`}
                  onClick={handleLinkClick}
                >
                  🛒 Cart {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
